import moment from 'moment'
import {
  findTransportationProfiles,
  findTotalTransportationProfiles
} from '@/services/transportation-profiles-service'
import {
  getVehicleKwhUsageDaily,
  saveVehicleKwhUsageBatch
} from '@/services/vehicle-kwh-usage-service'
import { addAlert } from '@/utilities/helpers'

function roundKwhValue (value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null
  }
  return Math.round(parsed * 1000) / 1000
}

function normalizeKwhInput (value) {
  if (value === undefined || value === null || value === '') {
    return ''
  }

  const rounded = roundKwhValue(value)
  if (rounded === null) {
    return null
  }

  return rounded.toFixed(3)
}

export default {
  name: 'advanced-vehicle-kwh-usage',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      selectedDate: moment.utc().subtract(1, 'day').format('YYYY-MM-DD'),
      selectedAgencyId: null,
      selectedProfile: null,
      buses: [],
      totalBuses: 0,
      usageByProfileId: {},
      draftByProfileId: {},
      originalByProfileId: {},
      rowStatusByProfileId: {},
      savingByProfileId: {},
      undoByProfileId: {},
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 25,
        sortBy: 'licensePlateNumber'
      },
      headers: [
        { text: 'License Plate', value: 'licensePlateNumber', width: '34%' },
        { text: 'Vehicle kWh Usage', value: 'kwhInput', sortable: false, width: '280px' },
        { text: 'Status', value: 'status', sortable: false, width: '260px' },
        { text: 'Save', value: 'actions', sortable: false, width: '96px' }
      ]
    }
  },
  computed: {
    agencyOptions () {
      return this.$store.getters.getAgencies || []
    },
    anyRowSaving () {
      const savingByProfileId = this.savingByProfileId || {}
      return Object.keys(savingByProfileId).some(function (key) {
        return !!savingByProfileId[key]
      })
    },
    formattedSelectedDateHeading () {
      const parsed = moment.utc(this.selectedDate, 'YYYY-MM-DD', true)
      if (!parsed.isValid()) {
        return 'Invalid date'
      }
      return parsed.format('dddd MMMM Do YYYY [(UTC)]')
    }
  },
  watch: {
    selectedAgencyId () {
      this.pagination.page = 1
      this.loadRows()
    },
    selectedDate () {
      this.loadRows()
    },
    selectedProfile () {
      this.pagination.page = 1
      this.loadRows()
    }
  },
  mounted () {
    this.loadRows()
  },
  methods: {
    resolveSelectedDate () {
      const parsed = moment.utc(this.selectedDate, 'YYYY-MM-DD', true)
      if (!parsed.isValid()) {
        return null
      }
      return parsed.format('YYYY-MM-DD')
    },
    buildTransportationWhere () {
      const where = {}
      if (this.selectedAgencyId) {
        where.agencyId = this.selectedAgencyId
      }
      if (this.selectedProfile && this.selectedProfile.id) {
        where.id = String(this.selectedProfile.id)
      }
      return where
    },
    async loadRows () {
      const resolvedDate = this.resolveSelectedDate()
      if (!resolvedDate) {
        addAlert({ message: 'Select a valid date', type: 'warning' })
        return
      }

      this.apiInProgress = true
      try {
        const where = this.buildTransportationWhere()
        const page = this.pagination.page || 1
        const limit = this.pagination.rowsPerPage || 25
        const offset = (page - 1) * limit
        const sortBy = this.pagination.sortBy || 'licensePlateNumber'
        const sortDirection = this.pagination.descending ? 'DESC' : 'ASC'
        const filter = {
          where: where,
          order: sortBy + ' ' + sortDirection,
          limit: limit,
          skip: offset
        }

        const results = await Promise.all([
          findTransportationProfiles(filter),
          findTotalTransportationProfiles({ where: where })
        ])

        this.buses = Array.isArray(results[0]) ? results[0] : []
        this.totalBuses = results[1] && typeof results[1].count === 'number'
          ? Number(results[1].count)
          : this.buses.length

        await this.loadUsageForVisibleBuses(resolvedDate)
      } catch (err) {
        addAlert({ message: err && err.message ? err.message : 'Failed to load vehicles', type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    async loadUsageForVisibleBuses (date) {
      const profileIds = this.buses.map((bus) => bus.id).filter(Boolean)
      if (!profileIds.length) {
        this.usageByProfileId = {}
        this.draftByProfileId = {}
        this.originalByProfileId = {}
        this.rowStatusByProfileId = {}
        this.savingByProfileId = {}
        this.undoByProfileId = {}
        return
      }

      const rows = await getVehicleKwhUsageDaily({
        date: date,
        transportationProfileIds: profileIds
      })

      const usageByProfileId = {}
      const draftByProfileId = {}
      const originalByProfileId = {}
      const rowStatusByProfileId = {}
      const savingByProfileId = {}
      const undoByProfileId = {}

      const safeRows = Array.isArray(rows) ? rows : []
      for (let i = 0; i < safeRows.length; i += 1) {
        const row = safeRows[i]
        const profileId = row && row.transportationProfileId ? String(row.transportationProfileId) : null
        if (!profileId) {
          continue
        }
        usageByProfileId[profileId] = row
      }

      for (let i = 0; i < this.buses.length; i += 1) {
        const bus = this.buses[i]
        const profileId = String(bus.id)
        const usageRow = usageByProfileId[profileId]
        const normalizedSaved = usageRow ? normalizeKwhInput(usageRow.kwh) : ''
        draftByProfileId[profileId] = normalizedSaved === null ? '' : normalizedSaved
        originalByProfileId[profileId] = {
          value: normalizedSaved === null ? '' : normalizedSaved,
          dateUpdated: usageRow && usageRow.dateUpdated ? String(usageRow.dateUpdated) : null
        }
        rowStatusByProfileId[profileId] = null
        savingByProfileId[profileId] = false
        undoByProfileId[profileId] = null
      }

      this.usageByProfileId = usageByProfileId
      this.draftByProfileId = draftByProfileId
      this.originalByProfileId = originalByProfileId
      this.rowStatusByProfileId = rowStatusByProfileId
      this.savingByProfileId = savingByProfileId
      this.undoByProfileId = undoByProfileId
    },
    formatRowStatus (profileId) {
      const status = this.rowStatusByProfileId[String(profileId)]
      if (!status) {
        return ''
      }

      if (status.type === 'saved') {
        return 'Saved'
      }
      if (status.type === 'cleared') {
        return 'Cleared'
      }
      if (status.type === 'conflict') {
        return 'Conflict (refresh row value)'
      }
      return status.message || ''
    },
    formatRowStatusClass (profileId) {
      const status = this.rowStatusByProfileId[String(profileId)]
      if (!status) {
        return ''
      }

      if (status.type === 'saved' || status.type === 'cleared') {
        return 'green--text'
      }
      if (status.type === 'conflict' || status.type === 'error') {
        return 'red--text'
      }
      if (status.type === 'info') {
        return 'blue--text'
      }
      return ''
    },
    async onRowBlur (profileId) {
      const key = String(profileId)
      const normalized = normalizeKwhInput(this.draftByProfileId[key])
      if (normalized === null) {
        this.rowStatusByProfileId[key] = {
          type: 'error',
          message: 'kWh must be a number >= 0'
        }
        return
      }

      this.draftByProfileId[key] = normalized
      if (this.rowStatusByProfileId[key] && this.rowStatusByProfileId[key].type === 'error') {
        this.rowStatusByProfileId[key] = null
      }

      await this.saveRow(profileId)
    },
    isRowDirty (profileId) {
      const key = String(profileId)
      const original = this.originalByProfileId[key] || { value: '' }
      const current = normalizeKwhInput(this.draftByProfileId[key])
      return (current || '') !== (original.value || '')
    },
    buildEntryPayload (profileId, normalized) {
      const key = String(profileId)
      const original = this.originalByProfileId[key] || { value: '', dateUpdated: null }

      if (!normalized) {
        return {
          transportationProfileId: key,
          clear: true,
          dateUpdated: original.dateUpdated
        }
      }

      return {
        transportationProfileId: key,
        kwh: Number(normalized),
        clear: false,
        dateUpdated: original.dateUpdated
      }
    },
    isSavingRow (profileId) {
      return !!this.savingByProfileId[String(profileId)]
    },
    canUndoRow (profileId) {
      const key = String(profileId)
      return this.undoByProfileId[key] !== null && this.undoByProfileId[key] !== undefined
    },
    focusKwhInput (profileId) {
      const fieldRef = this.$refs['kwhInput-' + String(profileId)]
      const field = Array.isArray(fieldRef) ? fieldRef[0] : fieldRef
      if (!field) {
        return
      }

      if (typeof field.focus === 'function') {
        field.focus()
        return
      }

      const input = field.$el ? field.$el.querySelector('input') : null
      if (input && typeof input.focus === 'function') {
        input.focus()
      }
    },
    undoRow (profileId) {
      const key = String(profileId)
      if (!this.canUndoRow(key)) {
        return
      }

      this.draftByProfileId[key] = this.undoByProfileId[key]
      this.rowStatusByProfileId[key] = {
        type: 'info',
        message: 'Previous value restored. Blur or save to apply.'
      }
      this.$nextTick(() => {
        this.focusKwhInput(key)
      })
    },
    async saveRow (profileId) {
      const resolvedDate = this.resolveSelectedDate()
      const key = String(profileId)
      if (!resolvedDate) {
        addAlert({ message: 'Select a valid date', type: 'warning' })
        return
      }

      if (this.isSavingRow(key)) {
        return
      }

      const original = this.originalByProfileId[key] || { value: '', dateUpdated: null }
      const normalized = normalizeKwhInput(this.draftByProfileId[key])
      if (normalized === null) {
        this.rowStatusByProfileId[key] = {
          type: 'error',
          message: 'kWh must be a number >= 0'
        }
        return
      }

      this.draftByProfileId[key] = normalized
      if ((normalized || '') === (original.value || '')) {
        return
      }

      this.savingByProfileId = Object.assign({}, this.savingByProfileId, { [key]: true })
      try {
        const response = await saveVehicleKwhUsageBatch({
          date: resolvedDate,
          entries: [this.buildEntryPayload(key, normalized)]
        })

        const results = response && Array.isArray(response.results) ? response.results : []
        for (let i = 0; i < results.length; i += 1) {
          const result = results[i] || {}
          const profileId = result.transportationProfileId ? String(result.transportationProfileId) : null
          if (!profileId) {
            continue
          }

          if (result.status === 'saved' || result.status === 'cleared') {
            const record = result.record || null
            const previousValue = original.value || ''
            this.usageByProfileId[profileId] = record
            const normalizedSaved = record ? normalizeKwhInput(record.kwh) : ''
            this.draftByProfileId[profileId] = normalizedSaved === null ? '' : normalizedSaved
            this.originalByProfileId[profileId] = {
              value: normalizedSaved === null ? '' : normalizedSaved,
              dateUpdated: record && record.dateUpdated ? String(record.dateUpdated) : null
            }
            this.undoByProfileId = Object.assign({}, this.undoByProfileId, { [profileId]: previousValue })
            this.rowStatusByProfileId[profileId] = {
              type: result.status
            }
          } else if (result.status === 'conflict') {
            const latest = result.record || null
            const latestValue = latest ? normalizeKwhInput(latest.kwh) : ''
            this.draftByProfileId[profileId] = latestValue === null ? '' : latestValue
            this.originalByProfileId[profileId] = {
              value: latestValue === null ? '' : latestValue,
              dateUpdated: latest && latest.dateUpdated ? String(latest.dateUpdated) : null
            }
            this.rowStatusByProfileId[profileId] = {
              type: 'conflict'
            }
          } else {
            this.rowStatusByProfileId[profileId] = {
              type: 'error',
              message: result.message || 'Failed'
            }
          }
        }
      } catch (err) {
        addAlert({ message: err && err.message ? err.message : 'Failed to save vehicle kWh usage', type: 'error' })
      } finally {
        this.savingByProfileId = Object.assign({}, this.savingByProfileId, { [key]: false })
      }
    },
    onPaginationChange () {
      this.loadRows()
    }
  }
}
